import * as React from 'react';
import { memo } from 'react';

import styled from 'styled-components';

/**
 * Знак профессии (ТЗ 0040, доводка).
 *
 * У профессии, в отличие от навыка, официального логотипа НЕ существует:
 * «DevOps engineer» — роль на рынке, а не продукт с брендбуком. Поэтому здесь
 * не логотипы, а небольшой набор нейтральных фигур, из которого профессии
 * назначается подходящая по смыслу: бесконечность DevOps'у, щит безопасности,
 * ракета своему стартапу.
 *
 * Ключ приходит из `GrowthTrack.icon`, а канонический список ключей живёт на
 * бэкенде (`career/constants.py: TRACK_ICONS`) — там же, где пикер в ERP берёт
 * подписи. Рисунки лежат здесь, потому что это SVG, а не данные.
 *
 * 🔴 Незнакомый и пустой ключ рисуются ПОРТФЕЛЕМ, а не пустотой. Профессия без
 * значка — дырка в сетке карточек; запасная фигура держит строй, и заодно
 * оператор сразу видит, что знак не выбран.
 *
 * Фигуры обводкой, а не заливкой: контурная линия одинаково читается и в 16px
 * рядом с названием, и в 40px на карточке, а рисовать её надёжнее — заливной
 * контур той же формы требует выворотки, где легко ошибиться на глаз.
 * Библиотеку иконок ради дюжины фигур не тащим: в ERP `lucide-react` 1.x уже
 * ломал сборку на webpack 4.
 */
export interface ITrackIcon {
  /** `GrowthTrack.icon`. Пустой и незнакомый — портфель. */
  icon?: string | null;
  /** Тусклый вариант: профессия не выбрана студентом. */
  muted?: boolean;
  size?: number;
}

/**
 * Ключ → фигуры в координатах 24×24.
 *
 * Список ключей ПРОФЕССИИ канонизирован бэкендом (`career/constants.py:
 * TRACK_ICONS`) — это то, что предлагает пикер в ERP. Здешняя карта фигур
 * ШИРЕ его на один ключ `group`: группы навыков на карте берут знаки отсюда
 * же, но профессией группа не является и в пикере профессий ей делать нечего.
 *
 * 🔴 Экспортируется намеренно — по той же причине, что и `CareerIcon.SHAPES`:
 * карта навыков рисует свои узлы прямым SVG, вложить туда React-компонент
 * можно только через `foreignObject`, а он ломает и масштабирование сцены, и
 * печать. Рисовать те же фигуры второй раз — гарантия, что через месяц у
 * группы на карте и в списке будут разные значки.
 */
export const SHAPES: Record<string, string[]> = {
  // < > — разработка
  code: ['M9.5 8 5 12l4.5 4', 'M14.5 8 19 12l-4.5 4'],
  // ∞ — DevOps
  infinity: [
    'M12 12c-2-2.67-4-4-6-4a4 4 0 1 0 0 8c2 0 4-1.33 6-4Z',
    'M12 12c2 2.67 4 4 6 4a4 4 0 0 0 0-8c-2 0-4 1.33-6 4Z',
  ],
  // окно браузера — frontend
  browser: [
    'M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z',
    'M3 9.5h18',
    'M6 7.2h.01',
    'M8.5 7.2h.01',
  ],
  // столбики — данные и аналитика
  chart: ['M4 20h16', 'M7.5 20V13', 'M12 20V5', 'M16.5 20V9'],
  // щит — безопасность
  shield: ['M12 3l8 3.5v5c0 4.6-3.2 8.4-8 9.5-4.8-1.1-8-4.9-8-9.5v-5L12 3z'],
  // ракета — свой продукт
  rocket: [
    'M12 2.5c2.4 2.6 3.7 5.9 3.7 9.4l-1.3 4.6h-4.8L8.3 11.9c0-3.5 1.3-6.8 3.7-9.4z',
    'M12 8.5h.01',
    'M9.6 16.5 6.5 19.5l3-.6z',
    'M14.4 16.5l3.1 3-3-.6z',
    'M11 19.5l1 2.5 1-2.5',
  ],
  // два блока — backend и инфраструктура
  server: ['M4 4.5h16v5H4z', 'M4 14.5h16v5H4z', 'M7.5 7h.01', 'M7.5 17h.01'],
  // цилиндр — база данных
  database: [
    'M12 3c4.4 0 8 1.3 8 3s-3.6 3-8 3-8-1.3-8-3 3.6-3 8-3z',
    'M4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6',
    'M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3',
  ],
  // чип — AI и машинное обучение
  cpu: [
    'M6 6h12v12H6z',
    'M9.5 9.5h5v5h-5z',
    'M9 3v3',
    'M15 3v3',
    'M9 18v3',
    'M15 18v3',
    'M3 9h3',
    'M3 15h3',
    'M18 9h3',
    'M18 15h3',
  ],
  // телефон — мобильная разработка
  mobile: [
    'M8 3h8a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z',
    'M12 17.8h.01',
  ],
  // облако
  cloud: ['M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z'],
  // галочка в круге — тестирование и качество
  check: ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z', 'M8 12.5l2.5 2.5L16 9.5'],
  // три сложенных бруска — группа навыков на карте
  //
  // 🔴 Не портфель: тот уже занят смыслом «профессия, которой не выбрали
  // знак», и группа с портфелем читалась бы как ещё одна профессия. И не
  // `server`: он законно достаётся группам «Linux и системы» и
  // «Инфраструктура».
  group: ['M4 5.5h16', 'M4 12h16', 'M4 18.5h16', 'M7.5 5.5v13'],
  // портфель — запасной знак и «значок не выбран»
  briefcase: [
    'M4 8h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z',
    'M9 8V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2',
  ],
};

export const COLORS: Record<string, string> = {
  code: '#3E63DD',
  infinity: '#0E7490',
  browser: '#7C3AED',
  chart: '#047857',
  shield: '#B91C1C',
  rocket: '#C2410C',
  server: '#334155',
  database: '#0891B2',
  cpu: '#6D28D9',
  mobile: '#0F766E',
  cloud: '#2563EB',
  check: '#15803D',
  group: '#475569',
  briefcase: '#64748B',
};

const MUTED = '#94A3B8';
const FALLBACK = 'briefcase';

const Box = styled.span<{ $size: number; $bg: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
`;

export const TrackIcon = memo(({ icon, muted = false, size = 32 }: ITrackIcon) => {
  const key = icon && SHAPES[icon] ? icon : FALLBACK;
  const color = muted ? MUTED : COLORS[key];

  return (
    // Декоративен: рядом всегда стоит название профессии, и читалка не должна
    // произносить его дважды.
    <Box $size={size} $bg={`${color}1F`} aria-hidden>
      <svg
        width={Math.round(size * 0.6)}
        height={Math.round(size * 0.6)}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round">
        {SHAPES[key].map((d) => (
          <path key={d} d={d} />
        ))}
      </svg>
    </Box>
  );
});
